import { infuse } from '../ioc-config';

const scriptsMap = new Map<string, Promise<boolean>>();
const stylesMap = new Map<string, Promise<boolean>>();

const appendScript = (url: string, attributes?: Record<string, any>): Promise<boolean> => {
  if (scriptsMap.has(url)) {
    return scriptsMap.get(url) as Promise<boolean>;
  }

  const targetElem = document.head;

  const promise = new Promise<boolean>((resolve, reject) => {
    const element = document.createElement('script');

    element.setAttribute('async', '');
    element.setAttribute('src', url);
    for (const attr in attributes) element.setAttribute(attr, attributes[attr]);
    element.onload = () => resolve(true);
    element.onerror = () => reject(new Error('Failed to load injected script element: ' + url));
    element.onabort = () => reject(new Error('Loading injected script element aborted: ' + url));
    targetElem.append(element);
  });

  scriptsMap.set(url, promise);

  return promise;
};

const appendStyle = (url: string, attributes?: Record<string, any>): Promise<boolean> => {
  if (stylesMap.has(url)) {
    return stylesMap.get(url) as Promise<boolean>;
  }

  const targetElem = getTargetElement();

  const promise = new Promise<boolean>((resolve, reject) => {
    const element = document.createElement('link');

    element.setAttribute('rel', 'stylesheet');
    for (const attr in attributes) element.setAttribute(attr, attributes[attr]);
    element.onload = () => resolve(true);
    element.onerror = () => reject(new Error('Failed to load injected style element'));
    targetElem.insertBefore(element, targetElem.firstChild);
    // IE quirk: dynamically injected link element does not trigger
    // href if already set in injected element.
    // Setting the href attribute after append, triggers properly.
    // -> https://stackoverflow.com/questions/1184950/dynamically-loading-css-stylesheet-doesnt-work-on-ie#answer-5541628
    element.setAttribute('href', url);
  });

  stylesMap.set(url, promise);

  return promise;
};

const getTargetElement = () => {
  const { componentTag } = infuse('config');

  // For AEM we have to return the shadowRoot of our component
  const elm = document.querySelector(componentTag);

  if (!elm || !elm.shadowRoot) {
    throw new Error(`couldn't find shadowRoot for component with tag ${componentTag}`);
  }

  return elm.shadowRoot;
};

export const loadScriptAsync = (url: string, attributes?: Record<string, any>): Promise<boolean> => {
  if (!url) return Promise.reject(new Error('loadScriptAsync() -> Missing URL Parameter'));

  return appendScript(url, attributes);
};

export const loadStyleAsync = (url: string, attributes?: Record<string, any>): Promise<boolean> => {
  if (!url) return Promise.reject(new Error('loadStyleAsync() -> Missing URL Parameter'));

  return appendStyle(url, attributes);
};

export const removeScriptElement = (url: string): void => {
  if (scriptsMap.has(url)) {
    scriptsMap.delete(url);
    document.querySelector(`script[src="${url}"]`)?.remove();
  }
};
